import { useTranslation } from 'react-i18next';

import { useHSOverlay } from '@core/hooks/useHSOverlay';

/**
 * What the last update changed in the First Label report.
 *
 * An update rewrites grades and conclusions on its own, with no approval
 * step, so the user must be able to see what moved and why: the summary, each
 * grade that changed with its new rationale, the figures the documents added,
 * the pages revised (and which had their chart or table data changed), the
 * risks added, and what the documents said that the report has no place for.
 */
export function ReportUpdateSummary({ isOpen, onClose, revision }) {
  const { t } = useTranslation();
  const { overlayId, overlayRef } = useHSOverlay(isOpen, onClose, 'hs-report-update-summary');
  const {
    summary, grades = [], overall = {}, pages = [], risks = [], not_placed: notPlaced = [],
    figures_added: figuresAdded = [],
  } = revision || {};
  const sectionTitle = 'mt-4 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]';

  return (
    <div
      id={overlayId}
      ref={overlayRef}
      // whitespace-normal: mounted inside the sidebar's nowrap status line.
      className="hs-overlay hidden size-full fixed top-0 start-0 z-80 overflow-x-hidden overflow-y-auto pointer-events-none whitespace-normal"
      role="dialog"
      tabIndex={-1}
      aria-labelledby={`${overlayId}-label`}
    >
      <div className="hs-overlay-open:mt-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all sm:max-w-xl sm:w-full m-3 sm:mx-auto min-h-[calc(100%-56px)] flex items-center">
        <div className="w-full flex flex-col bg-white border border-[var(--color-border)] shadow-sm rounded-lg pointer-events-auto">
          <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto text-start text-sm text-[var(--color-text)]">
            <h3 id={`${overlayId}-label`} className="text-lg font-semibold">
              {t('dataroom.firstLabel.changes.title')}
            </h3>
            <p className="mt-2 text-[var(--color-text-secondary)]">{summary}</p>

            {(grades.length > 0 || overall.new) && (
              <>
                <h4 className={sectionTitle}>{t('dataroom.firstLabel.changes.grades')}</h4>
                <ul className="mt-1 space-y-2" data-testid="report-update-grades">
                  {overall.new && (
                    <li className="font-medium">
                      {t('dataroom.firstLabel.changes.overall', { old: overall.old, new: overall.new })}
                    </li>
                  )}
                  {grades.map((grade) => (
                    <li key={grade.dimension}>
                      <span className="font-medium">
                        {t('dataroom.firstLabel.changes.grade', {
                          dimension: grade.dimension, old: grade.old, new: grade.new,
                        })}
                      </span>
                      <span className="block text-[var(--color-text-secondary)]">{grade.why}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {figuresAdded.length > 0 && (
              <>
                <h4 className={sectionTitle}>{t('dataroom.firstLabel.changes.figuresAdded')}</h4>
                <ul className="mt-1 space-y-2" data-testid="report-update-figures">
                  {figuresAdded.map((figure) => (
                    <li key={figure.key}>
                      <span className="font-medium">{figure.shown_as}</span>
                      <span className="block text-[var(--color-text-secondary)]">
                        {[figure.statement, figure.document].filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {risks.length > 0 && (
              <>
                <h4 className={sectionTitle}>{t('dataroom.firstLabel.changes.risks')}</h4>
                <ul className="mt-1 space-y-2" data-testid="report-update-risks">
                  {risks.map((risk) => (
                    <li key={risk.risk}>
                      <span className="font-medium">{risk.risk}</span>
                      <span className="block text-[var(--color-text-secondary)]">{risk.note}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {pages.length > 0 && (
              <>
                <h4 className={sectionTitle}>
                  {t('dataroom.firstLabel.changes.pages', { count: pages.length })}
                </h4>
                <ul className="mt-1 list-disc ps-5 space-y-1" data-testid="report-update-pages">
                  {pages.map((page) => (
                    <li key={page.key}>
                      {page.title || page.key}
                      {page.numbers && (
                        <span className="text-[var(--color-text-secondary)]">
                          {` (${t('dataroom.firstLabel.changes.numbers')})`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {notPlaced.length > 0 && (
              <>
                <h4 className={sectionTitle}>{t('dataroom.firstLabel.changes.notPlaced')}</h4>
                <ul className="mt-1 list-disc ps-5 space-y-1" data-testid="report-update-not-placed">
                  {notPlaced.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </>
            )}

            <p className="mt-4 text-xs text-[var(--color-text-secondary)]">
              {t('dataroom.firstLabel.changes.restore')}
            </p>
          </div>
          <div className="flex justify-end items-center gap-x-2 py-3 px-4 border-t border-[var(--color-border)]">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text)] shadow-sm hover:bg-[var(--color-bg-muted)] focus:outline-hidden"
            >
              {t('dataroom.firstLabel.changes.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
